import React from 'react';
import styled from 'styled-components/macro';
import ScrollableAnchor from 'react-scrollable-anchor';
import {
  Heading1,
  Heading2,
  Heading3,
  Bold,
  Paragraph,
  Span,
  LinkStyled,
  List,
} from '../../components/typography';
import {
  Container,
  Row,
  Col,
  Div,
} from '../../components/grid';
import { Image, ImagePopup } from '../../components/Image';
import {
  Price, PriceSum, MomsChange, Item,
} from '../../containers/commerce';
import { Table, Td, Th } from '../../components/tables';
import { ScreenSizeDetector } from '../../lib/helpers';
import SidemenuLayout from '../../layout/DefaultLayout/Layouts/SidemenuLayout';
import ProductViewKulsyre from '../../components/commerce/ProductViews/ProductViewKulsyre';

const Grid = styled.div`
  display: grid;
  grid-gap: 30px;
  margin: 10px 0;
  grid-template-columns: repeat(6, 1fr);

  grid-template-areas: 
    "grid3 grid3 grid4 grid4 grid5 grid5"
    "grid1 grid1 grid1 grid2 grid2 grid2";

  @media (max-width: 1199px) {
    grid-gap: 15px;
    margin: 0;
    grid-template-columns: repeat(6, 1fr);

    grid-template-areas:
      "grid3 grid3 grid3 grid3 grid3 grid3"
      "grid4 grid4 grid4 grid5 grid5 grid5"
      "grid1 grid1 grid1 grid1 grid1 grid1"
      "grid2 grid2 grid2 grid2 grid2 grid2";
  }
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;

    grid-template-areas: 
      "grid3"
      "grid5"
      "grid1"
      "grid2"
      "grid4";
  }
`;

export const Content = styled(Div)`
  background-color: ${({ theme }) => theme.colors.colorWhite};
  border: 1px solid ${({ theme }) => theme.colors.colorGreyLightAlt};
  border-radius: 15px;
  background-size: cover;
  background-position: bottom center;
  background-repeat: no-repeat;
  grid-area: ${({ gridArea }) => gridArea};
  padding: ${({ product }) => (product ? '0px' : '10px 15px')};
  overflow: hidden;

  & > svg {
    font-size: 40px;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.colorBlue};

    ${({ cart, theme }) => cart && `
      & > path {
        stroke: ${theme.colors.colorBlue};
      }
    `};
  }

  @media (max-width: 767px) {
    padding: ${({ product }) => (product ? '0px' : '10px')};
    min-height: ${({ image }) => (image ? '265px' : 'auto')};
  }
`;

const Title = styled(Paragraph)`
  font-size: 20px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  line-height: 1;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const Text = styled(Paragraph)`
  font-size: ${({ small }) => small ? '13px' : '16px'};
`;

const FaqDiv = styled.div`
  background-color: ${({ theme }) => theme.colors.colorOffWhite};
  border-radius: 15px;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  padding: 15px;
  height: calc(100% - 30px);
`;

const HeadCol = styled(Col)`
  text-align: center;

  & > * {
    text-align: center;
  }

  @media (max-width: 991px) {
    text-align: left;

    & > * {
      text-align: left;
    }
  }
`;

export default () => (
  <SidemenuLayout menuPicker="Kulsyre" activeSidemenu="Kulsyre">
    <Container white mb>
      <Row>
        <HeadCol xs={12}>
          <Heading1>Kulsyre</Heading1>
          <Heading2>Få ombyttet din kulsyreflaske eller køb en ny</Heading2>
          <Paragraph>
            Vi sælger både til <Bold>private</Bold> og <Bold>erhverv</Bold>, samt <Bold>offentlige virksomheder</Bold> via EAN.
          </Paragraph>
          <Paragraph>
            Der medfølger den rette mængde kulsyre, når du vælger at leje et <LinkStyled to="/fadoelsanlaeg">fadølsanlæg</LinkStyled> hos os.
          </Paragraph>
          <Paragraph>
            Flaskerne er af nøjagtig samme fabrikat og med samme gevind som dem bryggerierne bruger.
          </Paragraph>
        </HeadCol>
      </Row>
    </Container>
    <Container mb>
      <Row>
        <Col xs={12} padding="0">
          <Grid>
            <Content gridArea="grid1" product>
              <Item varenr="8940" display={ProductViewKulsyre} />
            </Content>
            <Content gridArea="grid2" product>
              <Item varenr="8944" display={ProductViewKulsyre} />
            </Content>
            <Content gridArea="grid3">
              <Title>Gratis levering</Title>
              <Text>
                Få gratis levering ved køb af min. 2 flasker.
              </Text>
              <Text>
                Eller hent selv på vores lager i Brøndby, hvor du kan købe kulsyre i vores åbningstid, <Span>hverdage 7 - 16</Span>.
              </Text>
              <Text>
                I forbindelse med leveringen tager vi gerne tomme flasker med retur og krediterer panten.
              </Text>
            </Content>
            <Content gridArea="grid4" bgImage="kulsyre.kulsyre-600x500-v1.png" image />
            <Content gridArea="grid5">
              <Title>Pant</Title>
              <Text>
                Kulsyreflasker du har købt hos os kan afleveres og panten tilbagebetales.
              </Text>
              <Text>
                På flasker med røde ventilhåndtag er panten <Span><Price varenr="8944" embPrice momsIndication /></Span>.
              </Text>
              <Text>
                På flasker med grønne ventilhåndtag er panten <Span><Price varenr="870" embPrice momsIndication /></Span>.
              </Text>
            </Content>
          </Grid>
        </Col>
      </Row>
    </Container>
    <Container white mb>
      <Row>
        <Col xs={12} sm={3} textCenter>
          <Div>
            <ScreenSizeDetector
              imageWidth={[[575, '150px'], [767, '105px'], [991, '158px'], [1199, '210px'], '255px']}
            >
              <ImagePopup name="kulsyre.kulsyre-engros1.jpg" />
            </ScreenSizeDetector>
          </Div>
          <Span fontSize="12px">33 kulsyre flasker på palle</Span>
        </Col>
        <Col xs={12} sm={9} id="engros">
          <Heading2>Mængderabat ved køb af flere flasker</Heading2>
          <Table lines1>
            <tbody>
              <tr>
                <Th>Antal</Th>
                <Th textRight>6kg</Th>
                <Th textRight>4kg</Th>
              </tr>
              <tr>
                <Td>2-5</Td>
                <Td textRight><Price varenr="8940" /> <MomsChange /></Td>
                <Td textRight><Price varenr="8944" /></Td>
              </tr>
              <tr>
                <Td>6-11</Td>
                <Td textRight><PriceSum varenr={[8940, 706]} itemPrice /> <MomsChange /></Td>
                <Td textRight><PriceSum varenr={[8944, 705]} itemPrice /></Td>
              </tr>
              <tr>
                <Td>12-39</Td>
                <Td textRight><PriceSum varenr={[8940, 712]} itemPrice /> <MomsChange /></Td>
                <Td textRight><PriceSum varenr={[8944, 711]} itemPrice /></Td>
              </tr>
              <tr>
                <Td>Hel palle - 4kg: 44 stk, 6kg: 40 stk</Td>
                <Td textRight><PriceSum varenr={[8940, 733]} itemPrice /> <MomsChange /></Td>
                <Td textRight><PriceSum varenr={[8944, 732]} itemPrice /></Td>
              </tr>
            </tbody>
          </Table>
          <Paragraph fontSize="12px">Fri levering i storkøbenhavn og omegn.</Paragraph>
          <Paragraph bold blue>Rabatten bliver tilføjet i kurven ved både 6 og 12 stk.</Paragraph>
          <Paragraph>
            Du kan også afhente kulsyreflaskerne selv i Brøndby, alle hverdage 7-16.
          </Paragraph>
          <Paragraph>
            Vi opkræver og tilbagebetaler pant, <Price varenr="8940" embPrice momsIndication /> per flaske.<br />
            Vi opkræver ingen lejedage, uanset hvor længe du beholder flasken.
          </Paragraph>
        </Col>
      </Row>
    </Container>
    <Container white mb>
      <Row>
        <Col xs={12} id="faq">
          <Heading2 textCenter>4 spørgsmål &amp; svar om kulsyre</Heading2>
          <Row>
            <Col xs={12} md={6} lg={6} xl={3}>
              <FaqDiv itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
                <Paragraph bold fontSize="15px" itemprop="name">
                  Hvad koster en ombytning?
                </Paragraph>
                <Div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
                  <Paragraph itemprop="text" marginBottom="0px">
                    Ombytning af 6kg flaske koster <Price varenr="8940" /> <MomsChange />
                  </Paragraph>
                </Div>
              </FaqDiv>
            </Col>
            <Col xs={12} md={6} lg={6} xl={3}>
              <FaqDiv itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
                <Paragraph bold fontSize="15px" itemprop="name">
                  Er der pant på kulsyre flasker?
                </Paragraph>
                <Div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
                  <Paragraph itemprop="text" marginBottom="0px">
                    Ja, panten er <Price varenr="8940" embPrice /> <MomsChange /> på både 4 og 6 kg flasker.
                  </Paragraph>
                </Div>
              </FaqDiv>
            </Col>
            <Col xs={12} md={6} lg={6} xl={3}>
              <FaqDiv itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
                <Paragraph bold fontSize="15px" itemprop="name">
                  Kan private bare komme forbi og købe?
                </Paragraph>
                <Div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
                  <Paragraph itemprop="text" marginBottom="0px">
                    Ja, alle er velkomne, åbent hverdage mellem 7 og 16 - vi tager dankort.
                  </Paragraph>
                </Div>
              </FaqDiv>
            </Col>
            <Col xs={12} md={6} lg={6} xl={3}>
              <FaqDiv itemscope itemprop="mainEntity" itemtype="https://schema.org/Question">
                <Paragraph bold fontSize="15px" itemprop="name">
                  Hvor meget kulsyre bruges til fadøl?
                </Paragraph>
                <Div itemscope itemprop="acceptedAnswer" itemtype="https://schema.org/Answer">
                  <Paragraph itemprop="text" marginBottom="0px">
                    Der bruges ca. 200g kulsyre pr. fustage, 6 kg rækker altså til 25-30 fustager.
                  </Paragraph>
                </Div>
              </FaqDiv>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
    <Container white mb>
      <Row>
        <Col xs={3}>
          <Row>
            <Col xs={12} sm={6}>
              <Image name="kulsyre.kulsyre-yarany.jpg" alt="Kulsyreflaske 6 kg Carlsberg" title="Kulsyreflaske 6 kg Carlsberg" />
              <Paragraph fontSize="12px">6kg. flaske</Paragraph>
            </Col>
            <Col xs={12} sm={6}>
              <Image name="kulsyre.kulsyre-yara-blaa-4kg.jpg" alt="Kulsyreflaske 4 kg udgået Carlsberg" title="Kulsyreflaske 4 kg udgået Carlsberg" />
              <Paragraph fontSize="12px">4kg. flaske</Paragraph>
            </Col>
          </Row>
        </Col>
        <Col xs={9}>
          <Heading2>Carlsberg og Tuborg kulsyreflasker</Heading2>
          <Paragraph>Den grønne kulsyreflaske afbilledet her til venstre kan afleveres hos os.</Paragraph>
          <Paragraph>
            Den grønne kulsyreflaske bruges i dag af Carlsberg bryggerierne, den blå er den forrige
            model der er udgået pr. januar 2009 - Den har ikke længere værdi. Begge Co<sub>2</sub> flasker findes i
            forskellige størrelser, her er den grønne på 6 kg og
            den blå på 4 kg. Carlsberg fører også 10 kg Co<sub>2</sub> flasker.
          </Paragraph>
          <Paragraph>Panten på den grønne er <Price varenr="870" embPrice momsIndication />.</Paragraph>
          <Paragraph>Ved køb af kulsyre, fadøl eller andre drikkevarer kan panten fratrækkes regningen.</Paragraph>
          <Paragraph>Vi køber også Co<sub>2</sub> flasker i ren handel, men udbetaler da kun kr. 500,- pr. flaske.</Paragraph>
          <Paragraph>Kulsyreflasker med blåt håndtag er udgået og du kan ikke længere få penge for dem nogen steder.</Paragraph>
        </Col>
      </Row>
    </Container>
    <Container white mb>
      <Row>
        <Col xs={3}>
          <Row>
            <Col xs={12} textCenter>
              <Image name="kulsyre.trykflasker-advarsel.jpg" alt="Trykflasker Advarsel" title="Trykflasker Advarsel" />
              <Paragraph fontSize="12px" paddingTop="10px">Opbevar kulsyreflasker hvor de er nemme at fjerne ved brand</Paragraph>
            </Col>
          </Row>
        </Col>
        <Col xs={9}>
          <Heading2>Sikker opbevaring af trykflasker</Heading2>
          <Paragraph>
            Reglerne for opbevaring af kulsyreflasker fremgår under Arbejdstilsynets &quot;Bekendtgørelse om transportabelt trykbærende udstyr&quot;, som kan læses her <LinkStyled external to="https://at.dk/regler/bekendtgoerelser/transportabelt-trykbaerende-udstyr-289-sam/">https://at.dk/regler/...</LinkStyled>
          </Paragraph>
          <Paragraph>
            Her et hurtigt sammendrag:
          </Paragraph>
          <List>
            <li>Co2 flasker må ikke opbevares eller udsættes for mere end 65 graders varme</li>
            <li>Skal opbevares så de er nemme at fjerne ved brand</li>
            <li>Skal sikres mod væltning</li>
            <li>Der skal være et skilt, der oplyser at trykflasker skal fjernes ved brand.</li>
          </List>
          <Paragraph marginTop="10px">
            Skiltet kan købes hos <LinkStyled external to="https://www.josafety.dk/shop/trykflasker-fjernes-ved-4779p.html">JO Safety</LinkStyled>.
          </Paragraph>
        </Col>
      </Row>
    </Container>
    <Container white mb>
      <Row>
        <Col xs={12}>
          <Heading2>Kulsyreflasker vi <u>IKKE</u> tager imod</Heading2>
          <Paragraph>Vi indbytter kun lyse- eller mørkegrønne flasker med grå skuldre og grønne/sorte håndtag.</Paragraph>
          <Paragraph>Vi tager blandt andet ikke imod følgende kulsyreflasker (Tryk på billederne for at forstørre).</Paragraph>
          <Div inline marginRight="10px">
            <ImagePopup name="kulsyre.nejsyre-blaa-soelv.jpg" imageHeight="300px" popupImageHeight="800px" />
          </Div>
          <Div inline marginRight="10px">
            <ImagePopup name="kulsyre.nejsyre-sort-graa.jpg" imageHeight="300px" popupImageHeight="800px" />
          </Div>
          <Div inline>
            <ImagePopup name="kulsyre.kulsyre-yara-blaa-4kg.jpg" imageHeight="300px" popupImageHeight="800px" />
          </Div>
        </Col>
      </Row>
    </Container>
    <ScrollableAnchor id="gevind">
      <div>
        <Container white mb>
          <Row>
            <Col xs={3}>
              <Row>
                <Col xs={12} textCenter>
                  <ImagePopup name="kulsyre.kulsyre-gevind.png" alt="Gevind kulsyreflaske" title="Gevind kulsyreflaske" imageHeight="150px" popupImageHeight="800px" />
                  <Paragraph fontSize="12px" paddingTop="10px">Gevind på kulsyreflaske</Paragraph>
                </Col>
              </Row>
            </Col>
            <Col xs={9}>
              <Heading2>Gevind på kulsyreflasker.</Heading2>
              <Paragraph>
                Alle kulsyreflasker bruger samme gevind (gælder i hele europa, Afrika og Australasien).
              </Paragraph>
              <Paragraph>
                Gevindets størrelse er 21,8mm i diameter med en stigning på 14 omgange pr. tomme
                og er i overensstemmelse med den tyske standard DIN 477 No. 6 se følgende link&nbsp;
                <LinkStyled external to="https://industry.airliquide.co.uk/sites/industry_uk/files/2016/11/29/fittings_for_co2.pdf">
                  PDF - www.uk.airliquide.com
                </LinkStyled>
              </Paragraph>
              <Paragraph>
                Vores flasker har ikke dykrør.
              </Paragraph>
            </Col>
          </Row>
        </Container>
      </div>
    </ScrollableAnchor>
    <Container white mb>
      <Row>
        <Col xs={3}>
          <Row>
            <Col xs={12} sm={6}>
              <Image name="kulsyre.sikkerhedsdatablad1.jpg" alt="Sikkerhedsdatablad" title="Sikkerhedsdatablad" />
            </Col>
            <Col xs={12} sm={6}>
              <Image name="kulsyre.sikkerhedsdatablad2.jpg" alt="Sikkerhedsdatablad" title="Sikkerhedsdatablad" />
            </Col>
          </Row>
        </Col>
        <Col xs={9}>
          <Heading2>Sikkerhedsdatablad</Heading2>
          <Paragraph>
            <LinkStyled external to="/pdf/sikkerhedsdatablad-kulsyre.pdf">Sikkerhedsdatablad for komprimeret kulsyre i trykflasker</LinkStyled><br />
            <LinkStyled external to="/pdf/co2-datablad.pdf">Datablad for levnedsmiddel CO2</LinkStyled>
          </Paragraph>
        </Col>
      </Row>
    </Container>
    <Container white mb>
      <Row>
        <Col xs={12} sm={6}>
          <Heading2>Forudsætning for teoretisk beregning af kulsyre- og mixgas-forbrug</Heading2>
          <Paragraph>
            Ideal gas loven siger: p*v=n*R*T hvor
          </Paragraph>
          <Paragraph>
            p: tryk i Pa (1 bar = 10^5 Pa)<br />
            v: volumen i m3<br />
            n: stofmængde i mol<br />
            R: konstant = 8,314<br />
            T: temperatur i K<br />
            Nitrogen g/moL: 14,01<br />
            Carbon g/moL: 12,01<br />
            Oxygen g/moL: 16,00
          </Paragraph>
          <Heading3>Uddybende specifikation af kulsyreforbrug til fadøl</Heading3>
          <Paragraph>
            <Bold>Kulsyregas forbrug pr. 25 liters fustage</Bold><br />
            Ved eksempelvis Grøn Tuborg fadøl, hvor arbejdstrykket bør være 3,0 bar fås:<br />
            n = p*v/(R*T) = (3,0+1)*10^5*0,025/(8,314*(273+20)) = 4,11 mol<br />
            Vægten af kulsyregassen bliver altså: 4,11 mol * 44 g/mol &#8776; 181 g<br />
            <Bold>Flasken rækker til: 6000g / 181g &#8776; 33 fustager.</Bold><br />
            <Span fontSize="12px">Tallene er teoretiske, der vil i praksis altid være et mindre spild.</Span>
          </Paragraph>
        </Col>
        <Col xs={12} sm={6}>
          <Image name="kulsyre.kulsyre-diagram.jpg" alt="Kulsyre Diagram" title="Kulsyre Diagram" />
        </Col>
      </Row>
    </Container>
    <Container white>
      <Row>
        <Col xs={12} md={6}>
          <Heading3>Uddybende specifikation af blandingsgas (30% CO<sub>2</sub>, 70% N<sub>2</sub>)</Heading3>
          <Paragraph>
            I en ny flaske blandingsglas er trykket 140 bar.<br />
            Indholdet er normalt gasformigt, blandingen skal ned på -37 °C før den første dråbe dannes.<br />
            Rumfanget af flasken er 8 liter (størrelse som en 6kg kulsyreflaske).
          </Paragraph>
          <Paragraph>
            <Bold>Mængden i en fuld flaske vil således være:</Bold><br />

            n = p*v/(R*T) = (200+1)*10^5*0,008/(8,314*(273+20)) = 66,0 mol<br />

            Molvægten af blandingsgas: M = 0,7*28+0,3*44 = 33 g/mol<br />

            Indhold bliver altså: 46,3 mol * 33 g/mol = <Bold>2,18 kg</Bold>
          </Paragraph>
          <Paragraph>
            <Bold>Mixgas forbrug pr. 30 liters fustage</Bold><br />
            Ved eksempelvis Guinness fadøl, hvor arbejdstrykket bør være 3,5 bar fås:<br />
            n = p*v/(R*T) = (3,5+1)*10^5*0,03/(8,314*(273+20)) = 5,54 mol<br />
            Vægten af mixgassen bliver altså: 5,54 mol * 33 g/mol = 183 g<br />
            <Bold>Flasken rækker til: 2178g / 183g &#8776; 12 fustager.</Bold><br />
            <Span fontSize="12px">Tallene er teoretiske, der vil i praksis altid være et mindre spild.</Span>
          </Paragraph>
        </Col>
        <Col xs={12} md={6}>
          <Paragraph floatLeft>
            <Image name="kulsyre.blandingsgas.png" alt="Blandingsgas" title="Blandingsgas" />
          </Paragraph>
          <Paragraph floatLeft margin="30px 0 0 30px">
            <Bold>Blandingsgas (30% CO<sub>2</sub>, 70% N<sub>2</sub>)</Bold><br />
            Fuldvægt: 15,80 kg.<br />
            8 liters flaske.<br />
            Pris: kr. <Price varenr="8945" />
          </Paragraph>
        </Col>
      </Row>
    </Container>
  </SidemenuLayout>
);
